import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { api } from '@/api';
import { toast } from 'sonner';
import { AlertCircle, FileText, ArrowRight, Loader2, Calendar, Download, CalendarClock, Mail, MessageSquare, BookOpen, Trash2, AlertTriangle, CheckCircle, Upload, X, Clock, ChevronDown, ChevronUp, Play, Paperclip, History, ExternalLink, Edit, Eye, Save, Bell, ShieldAlert, Scale, Lock, CheckCircle2, Info } from 'lucide-react';
import EmailHistoryPanel from '@/components/EmailHistoryPanel';
import EmailToEventPanel from '@/components/EmailToEventPanel';
import CorrespondenceLog from '@/components/CorrespondenceLog';
import TimelineNarrativePanel from '@/components/TimelineNarrativePanel';
import UnifiedCommunicationLog from '@/components/correspondence/UnifiedCommunicationLog';
import { useAuth } from '@/context/AuthContext';
import usePermissions from '@/hooks/usePermissions';
import { useTerminology } from '@/hooks/useTerminology';
import ContractualClassificationPanel from '@/components/ContractualClassificationPanel';

// Contract clause definitions per contract type
const CONTRACT_CLAUSES = {
  JBCC: [
    { id: 'jbcc_23.1', clause: 'Clause 23.1', title: 'Date for Practical Completion', description: 'Defines the contractual date for completion of works.' },
    { id: 'jbcc_23.2', clause: 'Clause 23.2', title: 'Revised Date for Practical Completion', description: 'Extension of time due to qualifying causes.' },
    { id: 'jbcc_23.3', clause: 'Clause 23.3', title: 'Delay Damages', description: 'Damages payable for late completion.' },
    { id: 'jbcc_23.7', clause: 'Clause 23.7', title: 'Non-compliance with Requirements', description: 'Penalties for failure to meet practical completion requirements.' },
    { id: 'jbcc_24.0', clause: 'Clause 24', title: 'Final Completion', description: 'Requirements for achieving final completion after practical completion.' },
    { id: 'jbcc_29.0', clause: 'Clause 29', title: 'Default by Contractor', description: 'Employer remedies when contractor defaults.' },
    { id: 'jbcc_30.0', clause: 'Clause 30', title: 'Default by Employer', description: 'Contractor remedies when employer defaults.' },
    { id: 'jbcc_15.0', clause: 'Clause 15', title: 'Site Instructions', description: 'Authority and obligations regarding site instructions.' },
    { id: 'jbcc_17.0', clause: 'Clause 17', title: 'Revision of the Works (Variations)', description: 'Procedures for issuing and valuing variations.' },
    { id: 'jbcc_6.10', clause: 'Clause 6.10', title: 'Adjustment of Contract Value', description: 'Mechanisms for adjusting the contract price.' },
    { id: 'jbcc_37.0', clause: 'Clause 37', title: 'Dispute Resolution', description: 'Procedures for resolving disputes between parties.' },
  ],
  FIDIC: [
    { id: 'fidic_1.9', clause: 'Clause 1.9', title: 'Delayed Drawings or Instructions', description: 'Obligations regarding timely provision of drawings and instructions.' },
    { id: 'fidic_2.1', clause: 'Clause 2.1', title: 'Right of Access to the Site', description: 'Employer obligation to provide site access.' },
    { id: 'fidic_8.4', clause: 'Clause 8.4', title: 'Extension of Time for Completion', description: 'Entitlement to extension of time.' },
    { id: 'fidic_8.7', clause: 'Clause 8.7', title: 'Delay Damages', description: 'Damages payable for failure to complete on time.' },
    { id: 'fidic_10.1', clause: 'Clause 10.1', title: 'Taking Over of the Works', description: 'Procedures for taking over completed works.' },
    { id: 'fidic_13.1', clause: 'Clause 13.1', title: 'Right to Vary', description: 'Engineer authority to issue variations.' },
    { id: 'fidic_13.3', clause: 'Clause 13.3', title: 'Variation Procedure', description: 'Procedures for implementing variations.' },
    { id: 'fidic_16.1', clause: 'Clause 16.1', title: 'Suspension by Contractor', description: 'Contractor right to suspend work.' },
    { id: 'fidic_17.4', clause: 'Clause 17.4', title: 'Employer Risk Consequences', description: 'Consequences of risks borne by the employer.' },
    { id: 'fidic_19.1', clause: 'Clause 19.1', title: 'Force Majeure', description: 'Definition and consequences of force majeure events.' },
    { id: 'fidic_20.1', clause: 'Clause 20.1', title: "Contractor's Claims", description: 'Procedure for contractor claims for additional payment or time.' },
  ],
  NEC: [
    { id: 'nec_60.1', clause: 'Clause 60.1', title: 'Compensation Events', description: 'Events that entitle the contractor to additional time or money.' },
    { id: 'nec_61.1', clause: 'Clause 61.1', title: 'Notifying Compensation Events', description: 'Contractor obligation to notify compensation events.' },
    { id: 'nec_61.3', clause: 'Clause 61.3', title: "Project Manager's Notification", description: 'PM obligation to notify compensation events.' },
    { id: 'nec_62.1', clause: 'Clause 62.1', title: 'Quotations for Compensation Events', description: 'Requirements for submitting quotations.' },
    { id: 'nec_63.1', clause: 'Clause 63.1', title: 'Assessing Compensation Events', description: 'Methods for assessing compensation events.' },
    { id: 'nec_64.1', clause: 'Clause 64.1', title: 'Assessment by Project Manager', description: "PM's assessment when contractor fails to submit quotation." },
    { id: 'nec_25.2', clause: 'Clause 25.2', title: 'Prevention', description: 'Events which prevent the contractor from completing works.' },
    { id: 'nec_36.1', clause: 'Clause 36.1', title: 'Acceleration', description: 'Procedures for accelerating the works.' },
    { id: 'nec_91.1', clause: 'Clause 91.1', title: 'Termination', description: 'Grounds and procedures for contract termination.' },
  ],
};

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [converting, setConverting] = useState(false);
  const [ceasedDialogOpen, setCeasedDialogOpen] = useState(false);
  const [ceasedDate, setCeasedDate] = useState('');
  const [ceasedDescription, setCeasedDescription] = useState('');
  const [ceasedAttachments, setCeasedAttachments] = useState([]);
  const [ceasedUploading, setCeasedUploading] = useState(false);
  const [ceasedIsDragging, setCeasedIsDragging] = useState(false);
  const [markingCeased, setMarkingCeased] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingImpact, setTogglingImpact] = useState(false);
  const [programmeImpactDialogOpen, setProgrammeImpactDialogOpen] = useState(false);
  const [programmeImpactReason, setProgrammeImpactReason] = useState('');
  const [programmeImpactDate, setProgrammeImpactDate] = useState('');
  const [impactReasonCategory, setImpactReasonCategory] = useState('');
  const [impactAttachmentIds, setImpactAttachmentIds] = useState([]);
  
  // Clause selection state for Convert to Notice
  const [clauseDialogOpen, setClauseDialogOpen] = useState(false);
  const [selectedClause, setSelectedClause] = useState('');
  
  // Notice readiness
  const [noticeReadiness, setNoticeReadiness] = useState(null);
  const [projectContractType, setProjectContractType] = useState('JBCC');
  
  // Timeline state
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [expandedTimelineItems, setExpandedTimelineItems] = useState({});
  
  const ceasedFileInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';
  const permissions = usePermissions();
  const { t, eventLabel, noticeLabel, claimLabel, isNEC } = useTerminology(event?.project_id);
  // Only allow editing if user has permission (contractors, admins) - not professional_team
  const canEditEvent = permissions.canEditEvents();

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    event_type: '',
    event_family: '',
    event_sub_type: '',
    narrative: '',
    responsible_party: '',
    responsible_party_detail: '',
    start_date: '',
    end_date: '',
    contract_clause: ''
  });
  const [saving, setSaving] = useState(false);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);

  // Taxonomy state for edit mode
  const [editTaxonomy, setEditTaxonomy] = useState([]);
  const [editSubTypes, setEditSubTypes] = useState([]);

  // Calculate days remaining (calendar and working days)
  const calculateDaysRemaining = (deadline) => {
    if (!deadline) return { calendar: 0, working: 0 };
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();
    const calendarDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Calculate working days (exclude weekends)
    let workingDays = 0;
    let currentDate = new Date(now);
    while (currentDate < deadlineDate) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Sunday (0) or Saturday (6)
        workingDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return { calendar: Math.max(0, calendarDays), working: Math.max(0, workingDays) };
  };

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  useEffect(() => {
    api.events.getTaxonomy().then(res => {
      setEditTaxonomy(res.data?.taxonomy || []);
    }).catch(() => {});
  }, []);


  useEffect(() => {
    if (activeTab === 'timeline' && eventId) {
      loadTimeline();
    }
  }, [activeTab, eventId]);

  const loadEvent = async () => {
    try {
      const response = await api.events.getById(eventId);
      setEvent(response.data);
      // Initialize edit form with event data
      setEditForm({
        event_type: response.data.event_type || '',
        event_family: response.data.event_family || '',
        event_sub_type: response.data.event_sub_type || '',
        narrative: response.data.narrative || '',
        responsible_party: response.data.responsible_party || '',
        responsible_party_detail: response.data.responsible_party_detail || '',
        start_date: response.data.start_date ? response.data.start_date.split('T')[0] : '',
        end_date: response.data.end_date ? response.data.end_date.split('T')[0] : '',
        contract_clause: response.data.contract_clause || ''
      });
      // Fetch project to get contract type
      if (response.data.project_id) {
        try {
          const projRes = await api.projects.getById(response.data.project_id);
          setProjectContractType(projRes.data.contract_type || 'JBCC');
        } catch (e) {
          console.warn('Could not load project contract type:', e);
        }
      }
      setError(null);
      // Load notice readiness
      try {
        const readinessRes = await api.events.checkNoticeReadiness(eventId);
        setNoticeReadiness(readinessRes.data);
      } catch (e) {
        // Non-critical — readiness check is informational
      }
    } catch (err) {
      console.error('Failed to load event:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load event';
      setError(errorMessage);
      if (err.response?.status === 403) {
        toast.error('You do not have permission to view this event');
      } else if (err.response?.status === 404) {
        toast.error('Event not found');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.narrative || !editForm.responsible_party || !editForm.start_date) {
      toast.error('Please fill in required fields (Narrative, Responsible Party, Start Date)');
      return;
    }
    
    setSaving(true);
    try {
      await api.events.update(eventId, {
        event_type: editForm.event_sub_type || editForm.event_type,
        event_family: editForm.event_family,
        event_sub_type: editForm.event_sub_type || editForm.event_type,
        narrative: editForm.narrative,
        responsible_party: editForm.responsible_party,
        responsible_party_detail: editForm.responsible_party_detail,
        start_date: editForm.start_date,
        end_date: editForm.end_date || null,
        contract_clause: editForm.contract_clause
      });
      toast.success('Event updated successfully');
      setIsEditing(false);
      loadEvent(); // Reload to get fresh data
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form to original event data
    setEditForm({
      event_type: event.event_type || '',
      event_family: event.event_family || '',
      event_sub_type: event.event_sub_type || '',
      narrative: event.narrative || '',
      responsible_party: event.responsible_party || '',
      responsible_party_detail: event.responsible_party_detail || '',
      start_date: event.start_date ? event.start_date.split('T')[0] : '',
      end_date: event.end_date ? event.end_date.split('T')[0] : '',
      contract_clause: event.contract_clause || ''
    });
    setIsEditing(false);
  };

  const loadTimeline = async () => {
    setTimelineLoading(true);
    try {
      const response = await api.events.getTimeline(eventId);
      setTimeline(response.data.timeline || []);
    } catch (err) {
      console.error('Failed to load timeline:', err);
      toast.error('Failed to load timeline');
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleConvertToNotice = async () => {
    if (!selectedClause) {
      toast.error('Please select a contract clause before generating the notice');
      return;
    }
    setClauseDialogOpen(false);
    setConverting(true);
    const loadingToast = toast.loading('Generating notice with AI... This may take up to 60 seconds.');
    try {
      const response = await api.events.convertToNotice(eventId, selectedClause);
      toast.dismiss(loadingToast);
      toast.success('Notice generated successfully');
      navigate(`/notices/${response.data.notice_id}`);
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Convert to notice error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. The AI service may be busy. Please try again.');
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate notice';
        toast.error(errorMsg);
      }
    } finally {
      setConverting(false);
    }
  };

  const handleMarkCeased = async () => {
    if (!ceasedDate) {
      toast.error('Please select a date');
      return;
    }
    
    setMarkingCeased(true);
    try {
      // Upload attachments first if any
      let uploadedAttachments = [];
      if (ceasedAttachments.length > 0) {
        setCeasedUploading(true);
        const formData = new FormData();
        ceasedAttachments.forEach((file) => {
          formData.append('files', file);
        });
        
        try {
          const uploadRes = await api.upload.multiple(formData);
          uploadedAttachments = uploadRes.data.files || [];
        } catch (uploadErr) {
          console.error('Failed to upload attachments:', uploadErr);
          toast.error('Failed to upload attachments');
          setCeasedUploading(false);
          setMarkingCeased(false);
          return;
        }
        setCeasedUploading(false);
      }
      
      const response = await api.events.markCeased(
        eventId, 
        new Date(ceasedDate).toISOString(),
        ceasedDescription || null,
        uploadedAttachments
      );
      toast.success(`Delay recorded as ceased.${response.data.claim_deadline ? ` EOT Claim due by ${new Date(response.data.claim_deadline).toLocaleDateString()}` : ''}`);
      setCeasedDialogOpen(false);
      setCeasedDate('');
      setCeasedDescription('');
      setCeasedAttachments([]);
      loadEvent();
    } catch (error) {
      toast.error('Failed to mark delay as ceased');
    } finally {
      setMarkingCeased(false);
    }
  };

  // Ceased attachment handlers
  const handleCeasedFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validExtensions = ['.msg', '.eml', '.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.xlsx', '.xls'];
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return validExtensions.includes(ext);
    });
    
    if (validFiles.length !== files.length) {
      toast.warning('Some files were not accepted. Allowed: .msg, .eml, .pdf, .doc, .docx, .xlsx, images');
    }
    
    setCeasedAttachments(prev => [...prev, ...validFiles]);
  };

  const handleCeasedDroppedFiles = (files) => {
    const validExtensions = ['.msg', '.eml', '.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.xlsx', '.xls'];
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return validExtensions.includes(ext);
    });
    
    if (validFiles.length === 0) {
      toast.error('No valid files found. Allowed: .msg, .eml, .pdf, .doc, .docx, .xlsx, images');
      return;
    }
    
    if (validFiles.length !== files.length) {
      toast.warning('Some files were not accepted');
    }
    
    setCeasedAttachments(prev => [...prev, ...validFiles]);
    toast.success(`${validFiles.length} file(s) added`);
  };

  const removeCeasedAttachment = (index) => {
    setCeasedAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Timeline helpers
  const toggleTimelineItem = (itemId) => {
    setExpandedTimelineItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getTimelineIcon = (type) => {
    switch (type) {
      case 'event_created':
        return <Calendar className="h-4 w-4" />;
      case 'event_start':
        return <Play className="h-4 w-4" />;
      case 'deadline':
        return <AlertTriangle className="h-4 w-4" />;
      case 'notice_created':
        return <FileText className="h-4 w-4" />;
      case 'delay_ceased':
        return <CheckCircle className="h-4 w-4" />;
      case 'correspondence':
      case 'imported_email':
        return <Mail className="h-4 w-4" />;
      case 'claim_created':
        return <FileText className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getTimelineColor = (type, category) => {
    if (category === 'deadline') return 'border-amber-500 bg-amber-50 text-amber-700';
    if (category === 'document') return 'border-blue-500 bg-blue-50 text-blue-700';
    if (category === 'communication') return 'border-purple-500 bg-purple-50 text-purple-700';
    
    switch (type) {
      case 'event_created':
      case 'event_start':
        return 'border-green-500 bg-green-50 text-green-700';
      case 'delay_ceased':
        return 'border-teal-500 bg-teal-50 text-teal-700';
      default:
        return 'border-slate-400 bg-slate-50 text-slate-700';
    }
  };

  const formatTimelineDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.events.delete(eventId);
      toast.success('Event deleted successfully');
      navigate(`/projects/${event.project_id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete event';
      toast.error(message);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleToggleProgrammeImpact = async (newValue) => {
    // If turning OFF (no longer on critical path), show dialog for reason + evidence
    if (!newValue) {
      setProgrammeImpactDate(new Date().toISOString().split('T')[0]);
      setImpactReasonCategory('');
      setProgrammeImpactReason('');
      setImpactAttachmentIds([]);
      setProgrammeImpactDialogOpen(true);
      return;
    }
    
    // If restoring to critical path, also show dialog
    setProgrammeImpactReason('');
    setImpactReasonCategory('');
    setImpactAttachmentIds([]);
    setProgrammeImpactDate('');
    setProgrammeImpactDialogOpen(true);
  };

  const handleConfirmImpactChange = async () => {
    const isRestoring = event?.still_impacting_programme === false;
    const isRemoving = event?.still_impacting_programme !== false;
    
    if (isRemoving && !impactReasonCategory) {
      toast.error('Please select a reason category');
      return;
    }
    if (!programmeImpactReason.trim()) {
      toast.error('Please provide notes explaining this change');
      return;
    }
    
    setTogglingImpact(true);
    try {
      const newValue = isRestoring; // If currently off, we're restoring (true). If on, we're removing (false).
      const res = await api.events.toggleProgrammeImpact(eventId, newValue, {
        reason: programmeImpactReason,
        date: programmeImpactDate,
        impact_reason_category: impactReasonCategory,
        attachment_ids: impactAttachmentIds,
      });
      setEvent(prev => ({ 
        ...prev, 
        still_impacting_programme: newValue,
        programme_impact_notes: programmeImpactReason,
        programme_impact_ended_date: programmeImpactDate,
        impact_reason_category: impactReasonCategory,
        timeline: [...(prev.timeline || []), res.data?.timeline_entry].filter(Boolean),
      }));
      toast.success(newValue ? 'Restored to critical path' : 'Removed from critical path');
      setProgrammeImpactDialogOpen(false);
    } catch (error) {
      console.error('Failed to update critical path status:', error);
      toast.error('Failed to update critical path status');
    } finally {
      setTogglingImpact(false);
    }
  };

  const downloadCalendar = async (type) => {
    try {
      const token = localStorage.getItem('token');
      let url, filename;
      if (type === 'notice') {
        url = api.events.downloadNoticeCalendar(eventId);
        filename = `Notice_Deadline_${event.event_type}.ics`;
      } else if (type === 'determination') {
        url = api.events.downloadDeterminationCalendar(eventId);
        filename = `Determination_Deadline_${event.event_type}.ics`;
      } else {
        url = api.events.downloadClaimCalendar(eventId);
        filename = `Claim_Deadline_${event.event_type}.ics`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Calendar file downloaded with reminders (7 days, 3 days, 1 day before & on deadline day).');
    } catch (error) {
      toast.error('Failed to download calendar file');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-slate-500 font-mono">Loading event...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!event) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-12 text-center">
            <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-barlow font-bold uppercase mb-2">
              {error?.includes('permission') || error?.includes('Access denied') 
                ? 'Access Denied' 
                : 'Event Not Found'}
            </h3>
            {error && (
              <p className="text-slate-500 mb-4">{error}</p>
            )}
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-orange-100 text-orange-700 border-orange-200',
      pending_classification: 'bg-amber-100 text-amber-800 border-amber-300',
      clause_assigned: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      ready_for_notice: 'bg-teal-100 text-teal-700 border-teal-200',
      notice_created: 'bg-blue-100 text-blue-700 border-blue-200',
      notice_issued: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      claim_created: 'bg-violet-100 text-violet-700 border-violet-200',
      claim_submitted: 'bg-purple-100 text-purple-700 border-purple-200',
      approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      pending_approval: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    };
    return styles[status] || styles.draft;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="event-detail">
        <div className="mb-8">
          <Button
            onClick={() => navigate(`/projects/${event.project_id}`)}
            variant="ghost"
            className="mb-4 text-slate-600 hover:text-slate-900"
          >
            ← Back to Project
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-barlow font-bold uppercase tracking-tight mb-2">
                {event.event_number || event.event_type}
              </h1>
              <div className="flex items-center gap-2 sm:gap-3 text-sm text-slate-600 flex-wrap">
                <Badge className={`${getStatusBadge(event.status)} border`}>
                  {event.status.replace(/_/g, ' ').toUpperCase()}
                </Badge>
                <span className="hidden sm:inline">|</span>
                {event.event_family ? (
                  <span className="text-slate-700 font-medium">{event.event_family}</span>
                ) : (
                  <span className="text-slate-700">{event.event_type}</span>
                )}
                {event.event_source && event.event_source !== 'manual' && (
                  <>
                    <span className="hidden sm:inline">|</span>
                    <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs uppercase">
                      Source: {event.event_source.replace(/_/g, ' ')}
                    </Badge>
                  </>
                )}
                <span className="hidden sm:inline">|</span>
                <span className="font-mono">{new Date(event.start_date).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap shrink-0">
              {/* Mark Ceased button - only for users with canMarkCeased permission */}
              {!event.notice_id && permissions.canMarkCeased() && (
                <Dialog open={ceasedDialogOpen} onOpenChange={(open) => {
                  setCeasedDialogOpen(open);
                  if (!open) {
                    setCeasedDate('');
                    setCeasedDescription('');
                    setCeasedAttachments([]);
                  }
                }}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs"
                        data-testid="mark-ceased-button"
                      >
                        <CalendarClock className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Mark Ceased</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-barlow font-bold uppercase">Mark {eventLabel} as Ceased</DialogTitle>
                        <DialogDescription>
                          Record when the {eventLabel.toLowerCase()} ceased and optionally attach supporting documentation.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Date Delay Ceased *</Label>
                          <Input
                            type="date"
                            value={ceasedDate}
                            onChange={(e) => setCeasedDate(e.target.value)}
                            className="rounded-sm font-mono"
                            data-testid="ceased-date-input"
                          />
                          <p className="text-xs text-slate-500 mt-2">
                            This will calculate the 40-day EOT claim deadline (JBCC 23.5)
                          </p>
                        </div>
                        
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Description / Notes</Label>
                          <Textarea
                            value={ceasedDescription}
                            onChange={(e) => setCeasedDescription(e.target.value)}
                            placeholder="Describe how/why the delay ceased, any relevant details..."
                            rows={3}
                            className="rounded-sm"
                            data-testid="ceased-description-input"
                          />
                        </div>
                        
                        {/* Drag & Drop Attachments */}
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                            <Upload className="h-3 w-3 inline mr-1" />
                            Supporting Documents
                          </Label>
                          <div
                            className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
                              ceasedIsDragging 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-slate-300 bg-slate-50'
                            }`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setCeasedIsDragging(true);
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setCeasedIsDragging(false);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setCeasedIsDragging(false);
                              const files = Array.from(e.dataTransfer.files);
                              handleCeasedDroppedFiles(files);
                            }}
                            data-testid="ceased-drop-zone"
                          >
                            <p className="text-xs text-slate-500 mb-3">
                              <strong>Drag & drop</strong> emails (.msg, .eml), PDFs, or documents here.
                            </p>
                            
                            <div className="text-center py-3">
                              {ceasedIsDragging ? (
                                <div className="text-blue-600">
                                  <Upload className="h-8 w-8 mx-auto mb-2 animate-bounce" />
                                  <p className="font-medium">Drop files here</p>
                                </div>
                              ) : (
                                <>
                                  <input
                                    type="file"
                                    ref={ceasedFileInputRef}
                                    multiple
                                    accept=".msg,.eml,.pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.gif"
                                    onChange={handleCeasedFileChange}
                                    className="hidden"
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => ceasedFileInputRef.current?.click()}
                                    disabled={ceasedUploading}
                                  >
                                    <Upload className="h-4 w-4 mr-2" />
                                    Choose Files
                                  </Button>
                                  <p className="text-xs text-slate-400 mt-2">or drag & drop files</p>
                                </>
                              )}
                            </div>
                            
                            {/* Selected Files */}
                            {ceasedAttachments.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {ceasedAttachments.map((file, index) => (
                                  <div 
                                    key={index}
                                    className="flex items-center justify-between p-2 bg-white rounded border"
                                    data-testid={`ceased-attachment-${index}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      {file.name.endsWith('.msg') || file.name.endsWith('.eml') ? (
                                        <Mail className="h-4 w-4 text-blue-500" />
                                      ) : file.name.endsWith('.pdf') ? (
                                        <FileText className="h-4 w-4 text-red-500" />
                                      ) : (
                                        <Paperclip className="h-4 w-4 text-slate-500" />
                                      )}
                                      <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                                      <span className="text-xs text-slate-400">
                                        ({(file.size / 1024).toFixed(1)} KB)
                                      </span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeCeasedAttachment(index)}
                                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          onClick={handleMarkCeased}
                          disabled={markingCeased || ceasedUploading}
                          className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                          data-testid="confirm-ceased-button"
                        >
                          {markingCeased || ceasedUploading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              {ceasedUploading ? 'Uploading...' : 'Processing...'}
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Confirm Cessation
                            </>
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
              )}
              
              {/* Convert to Notice button - blocked if classification pending */}
              {!event.notice_id && permissions.canConvertEventToNotice() && (
                <>
                  {/* Warning banner if classification is pending */}
                  {event.notice_generation_locked && (
                    <div className="w-full bg-amber-50 border border-amber-300 rounded-sm p-3 flex items-start gap-2" data-testid="classification-warning-banner">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-800">Contractual Classification Required</p>
                        <p className="text-xs text-amber-700">This event was created from an external source ({(event.event_source || 'unknown').replace(/_/g, ' ')}). Complete the Contractual Classification section below before generating a notice.</p>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={() => {
                      if (event.notice_generation_locked) {
                        toast.error('Please complete the Contractual Classification first');
                        return;
                      }
                      setSelectedClause(event.contract_clause || '');
                      setClauseDialogOpen(true);
                    }}
                    disabled={converting || event.notice_generation_locked}
                    data-testid="convert-to-notice-button"
                    className={`rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3 active:scale-95 transition-transform ${
                      event.notice_generation_locked 
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed' 
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {converting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating {noticeLabel}...
                      </>
                    ) : event.notice_generation_locked ? (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Classification Required
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        Convert to {noticeLabel}
                      </>
                    )}
                  </Button>

                  {/* Clause Selection Dialog */}
                  <Dialog open={clauseDialogOpen} onOpenChange={setClauseDialogOpen}>
                    <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Scale className="h-5 w-5 text-slate-700" />
                          Select Contract Clause
                        </DialogTitle>
                        <DialogDescription asChild>
                          <div>
                            Select the applicable contract clause before generating the formal notice.
                            Contract type: <Badge className="ml-1 bg-slate-100 text-slate-700 rounded-sm">{projectContractType}</Badge>
                          </div>
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-2 py-2" data-testid="clause-list">
                        {(CONTRACT_CLAUSES[projectContractType] || CONTRACT_CLAUSES.JBCC).map((clause) => {
                          const isSelected = selectedClause === `${clause.clause} - ${clause.title}`;
                          return (
                            <div
                              key={clause.id}
                              className={`p-3 rounded-md border cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
                                  : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50/50'
                              }`}
                              onClick={() => setSelectedClause(`${clause.clause} - ${clause.title}`)}
                              data-testid={`clause-option-${clause.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                  isSelected ? 'border-slate-900 bg-slate-900' : 'border-slate-300'
                                }`}>
                                  {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-sm text-slate-900">{clause.clause} — {clause.title}</p>
                                  <p className="text-xs text-slate-500 mt-0.5">{clause.description}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                          variant="outline"
                          onClick={() => setClauseDialogOpen(false)}
                          className="rounded-sm"
                          data-testid="clause-cancel-btn"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleConvertToNotice}
                          disabled={!selectedClause || converting}
                          className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                          data-testid="clause-confirm-btn"
                        >
                          {converting ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <FileText className="h-4 w-4 mr-2" />
                              Confirm & Generate Notice
                            </>
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              )}
              
              {event.notice_id && (
                <Button
                  onClick={() => navigate(`/notices/${event.notice_id}`)}
                  className="bg-secondary text-white hover:bg-secondary/90 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                >
                  View Notice
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
              
              {/* Delete Button - Admin Only */}
              {isAdmin && !event.notice_id && (
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-3"
                      data-testid="delete-event-button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="text-xl font-barlow font-bold uppercase text-red-600">Delete Event</DialogTitle>
                      <DialogDescription>
                        This will permanently delete this delay event and all associated correspondence, narratives, and email history.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                        <p className="text-sm text-red-700">
                          <strong>Warning:</strong> This action cannot be undone. The following will be deleted:
                        </p>
                        <ul className="text-sm text-red-600 mt-2 ml-4 list-disc">
                          <li>This delay event</li>
                          <li>All correspondence messages</li>
                          <li>Timeline narratives</li>
                          <li>Email history</li>
                        </ul>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          onClick={handleDelete}
                          disabled={deleting}
                          className="bg-red-600 text-white hover:bg-red-700"
                        >
                          {deleting ? 'Deleting...' : 'Delete Event'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>

        {/* Deadline Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Notice Deadline - Show until notice is created */}
          {event.notice_deadline && !event.notice_id && (
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300 rounded-sm p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-orange-900">Notice Deadline (JBCC 23.4.2)</h3>
                <Calendar className="h-5 w-5 text-orange-600" />
              </div>
              <p className="text-3xl font-barlow font-bold text-orange-900 mb-2">
                {new Date(event.notice_deadline).toLocaleDateString()}
              </p>
              {(() => {
                const days = calculateDaysRemaining(event.notice_deadline);
                return days.calendar > 0 ? (
                  <div className="text-sm text-orange-700 mb-4 space-y-1">
                    <p><strong>{days.working}</strong> working days left</p>
                    <p><strong>{days.calendar}</strong> calendar days left</p>
                  </div>
                ) : (
                  <p className="text-sm text-orange-700 mb-4 font-semibold">Deadline passed</p>
                );
              })()}
              <Button
                onClick={() => downloadCalendar('notice')}
                variant="outline"
                className="w-full border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-2"
              >
                <Download className="h-4 w-4 mr-2" />
                Add to Calendar
              </Button>
              <p className="text-xs text-orange-600 mt-2">Reminders: 7, 3 & 1 days before + day of deadline</p>
            </div>
          )}

          {/* Claim Deadline - Only show after notice created AND delay has ceased AND claim not yet submitted */}
          {event.claim_deadline && event.notice_id && event.delay_ceased_date && !event.claim_submitted_at && !(['submitted','in_adjudication','under_review','adjudication'].includes((event.claim_status || '').toLowerCase())) && (
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-sm p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-purple-900">Claim Deadline (JBCC 23.5)</h3>
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <p className="text-3xl font-barlow font-bold text-purple-900 mb-2">
                {new Date(event.claim_deadline).toLocaleDateString()}
              </p>
              {(() => {
                const days = calculateDaysRemaining(event.claim_deadline);
                return days.calendar > 0 ? (
                  <div className="text-sm text-purple-700 mb-4 space-y-1">
                    <p><strong>{days.working}</strong> working days left</p>
                    <p><strong>{days.calendar}</strong> calendar days left</p>
                  </div>
                ) : (
                  <p className="text-sm text-purple-700 mb-4 font-semibold">Deadline passed</p>
                );
              })()}
              <Button
                onClick={() => downloadCalendar('claim')}
                variant="outline"
                className="w-full border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-2"
              >
                <Download className="h-4 w-4 mr-2" />
                Add to Calendar
              </Button>
              <p className="text-xs text-purple-600 mt-2">Reminders: 7, 3 & 1 days before + day of deadline</p>
            </div>
          )}

          {/* Client Response Deadline - Shows after claim is submitted */}
          {event.determination_deadline && (event.claim_submitted_at || ['submitted','in_adjudication','under_review','adjudication'].includes((event.claim_status || '').toLowerCase())) && (
            <div data-testid="determination-deadline-box" className={`border-2 rounded-sm p-4 sm:p-6 ${
              (() => {
                const days = calculateDaysRemaining(event.determination_deadline);
                if (days.calendar < 0) return 'bg-gradient-to-br from-red-50 to-red-100 border-red-400';
                if (days.calendar <= 3) return 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-400';
                if (days.calendar <= 7) return 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300';
                return 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300';
              })()
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
                  {event.terminology?.decision_maker || 'Client'} Response Deadline
                </h3>
                {(() => {
                  const days = calculateDaysRemaining(event.determination_deadline);
                  if (days.calendar < 0) return <AlertTriangle className="h-5 w-5 text-red-600" />;
                  if (days.calendar <= 7) return <AlertCircle className="h-5 w-5 text-amber-600" />;
                  return <Clock className="h-5 w-5 text-blue-600" />;
                })()}
              </div>
              
              {(() => {
                const days = calculateDaysRemaining(event.determination_deadline);
                const isOverdue = days.calendar < 0;
                const colorClass = isOverdue ? 'text-red-900' : days.calendar <= 7 ? 'text-amber-900' : 'text-blue-900';
                const subtextColor = isOverdue ? 'text-red-700' : days.calendar <= 7 ? 'text-amber-700' : 'text-blue-700';
                
                return (
                  <>
                    <p className={`text-3xl font-barlow font-bold ${colorClass} mb-1`}>
                      {isOverdue ? (
                        <>{Math.abs(days.calendar)} days overdue</>
                      ) : (
                        <>{days.calendar} days left</>
                      )}
                    </p>
                    <div className="flex items-center gap-3 mb-3">
                      <p className={`text-sm ${subtextColor}`}>
                        {isOverdue ? (
                          <><strong>{Math.abs(days.working)}</strong> working days overdue</>
                        ) : (
                          <><strong>{days.working}</strong> working days remaining</>
                        )}
                      </p>
                      <span className="text-xs text-slate-400">
                        Due: {new Date(event.determination_deadline).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    
                    {/* Progress bar */}
                    {!isOverdue && (
                      <div className="w-full bg-slate-200 rounded-full h-2 mb-3">
                        <div 
                          className={`h-2 rounded-full transition-all ${
                            days.calendar <= 3 ? 'bg-red-500' : days.calendar <= 7 ? 'bg-amber-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.max(5, Math.min(100, 100 - (days.calendar / 28 * 100)))}%` }}
                        />
                      </div>
                    )}
                    
                    {isOverdue && (
                      <div className="bg-red-100 border border-red-300 rounded-sm p-3 mb-3">
                        <p className="text-xs font-bold text-red-800 uppercase">Response Period Expired</p>
                        <p className="text-xs text-red-700 mt-1">
                          The contractual response period has lapsed. Consider sending a formal reminder or escalating.
                        </p>
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-500 mb-4">
                      {event.contract_clause || 'Per contract terms'} — Awaiting determination from {event.terminology?.decision_maker || 'client'}
                    </p>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`text-xs rounded-sm ${isOverdue ? 'border-red-400 text-red-700 hover:bg-red-50' : 'border-slate-300'}`}
                        onClick={async () => {
                          try {
                            const res = await api.events.sendReminder(eventId);
                            const count = res.data?.emails_sent_to?.length || 0;
                            toast.success(`Reminder sent to ${count} recipient(s)`);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to send reminder');
                          }
                        }}
                        data-testid="send-claim-reminder-btn"
                      >
                        <Bell className="h-3.5 w-3.5 mr-1.5" />
                        Send Reminder
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs rounded-sm"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg';
                          input.onchange = async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              formData.append('tag', 'Claim Response');
                              await api.events.uploadDocument(eventId, formData);
                              toast.success(`Document "${file.name}" uploaded successfully`);
                              loadEvent();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to upload document');
                            }
                          };
                          input.click();
                        }}
                        data-testid="upload-claim-docs-btn"
                      >
                        <Upload className="h-3.5 w-3.5 mr-1.5" />
                        Upload Docs
                      </Button>
                      {isOverdue && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs rounded-sm border-red-400 text-red-700 hover:bg-red-50"
                          onClick={() => setActiveTab('correspondence')}
                          data-testid="escalate-claim-btn"
                        >
                          <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
                          Escalate
                        </Button>
                      )}
                      <Button
                        onClick={() => downloadCalendar('determination')}
                        size="sm"
                        variant="outline"
                        className="text-xs rounded-sm"
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Calendar
                      </Button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Notice Created - Show status after notice exists but delay not ceased */}
          {event.notice_id && !event.delay_ceased_date && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-sm p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-green-900">Notice Submitted</h3>
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-sm text-green-700 mb-4">
                Notice has been created. Mark the delay as ceased to calculate the claim deadline.
              </p>
              <Button
                onClick={() => navigate(`/notices/${event.notice_id}`)}
                variant="outline"
                className="w-full border-green-600 text-green-600 hover:bg-green-600 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-2"
              >
                <FileText className="h-4 w-4 mr-2" />
                View Notice
              </Button>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-slate-200 mb-6">
          <div className="flex space-x-1 overflow-x-auto">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === 'details'
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
              data-testid="tab-details"
            >
              <FileText className="h-4 w-4 inline mr-2" />
              Event Details
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === 'timeline'
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
              data-testid="tab-timeline"
            >
              <History className="h-4 w-4 inline mr-2" />
              Timeline
            </button>
            <button
              onClick={() => setActiveTab('correspondence')}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === 'correspondence'
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
              data-testid="tab-correspondence"
            >
              <MessageSquare className="h-4 w-4 inline mr-2" />
              Correspondence
            </button>
            <button
              onClick={() => setActiveTab('narrative')}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === 'narrative'
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
              data-testid="tab-narrative"
            >
              <BookOpen className="h-4 w-4 inline mr-2" />
              Narrative & Summary
            </button>
            <button
              onClick={() => setActiveTab('emails')}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === 'emails'
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
              data-testid="tab-emails"
            >
              <Mail className="h-4 w-4 inline mr-2" />
              Email History
            </button>
          </div>
        </div>

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6" data-testid="timeline-tab">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-slate-700" />
                <h2 className="text-xl font-barlow font-bold uppercase tracking-tight">Event Timeline</h2>
                <Badge variant="secondary" className="ml-2">{timeline.length} entries</Badge>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadTimeline}
                disabled={timelineLoading}
              >
                {timelineLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Clock className="h-4 w-4 mr-2" />
                    Refresh
                  </>
                )}
              </Button>
            </div>
            
            {timelineLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                <span className="ml-3 text-slate-500">Loading timeline...</span>
              </div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <History className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p>No timeline entries found.</p>
                <p className="text-sm">Timeline will populate as events occur.</p>
              </div>
            ) : (
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />
                
                <div className="space-y-4">
                  {timeline.map((entry, index) => {
                    const isExpanded = expandedTimelineItems[entry.id];
                    const colorClass = getTimelineColor(entry.type, entry.category);
                    
                    return (
                      <div key={entry.id || index} className="relative pl-14" data-testid={`timeline-entry-${index}`}>
                        {/* Timeline dot */}
                        <div className={`absolute left-4 w-5 h-5 rounded-full border-2 flex items-center justify-center ${colorClass}`}>
                          {getTimelineIcon(entry.type)}
                        </div>
                        
                        {/* Entry card */}
                        <div className={`border rounded-sm overflow-hidden ${
                          entry.attachments?.length > 0 || entry.details?.full_body 
                            ? 'cursor-pointer hover:shadow-md transition-shadow' 
                            : ''
                        }`}>
                          {/* Header - always visible */}
                          <div 
                            className={`p-4 ${entry.attachments?.length > 0 || entry.details?.full_body ? 'cursor-pointer' : ''}`}
                            onClick={() => {
                              if (entry.attachments?.length > 0 || entry.details?.full_body) {
                                toggleTimelineItem(entry.id);
                              }
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <h4 className="font-medium text-slate-900">{entry.title}</h4>
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {entry.type.replace('_', ' ')}
                                  </Badge>
                                  {entry.attachments?.length > 0 && (
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                                      <Paperclip className="h-3 w-3 mr-1" />
                                      {entry.attachments.length}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600 line-clamp-2">{entry.description}</p>
                                <p className="text-xs text-slate-400 mt-1 font-mono">
                                  {formatTimelineDate(entry.date)}
                                </p>
                              </div>
                              {(entry.attachments?.length > 0 || entry.details?.full_body) && (
                                <div className="ml-2">
                                  {isExpanded ? (
                                    <ChevronUp className="h-5 w-5 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 text-slate-400" />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Expanded content */}
                          {isExpanded && (
                            <div className="border-t border-slate-100 bg-slate-50">
                              {/* Full body text */}
                              {entry.details?.full_body && (
                                <div className="p-4 border-b border-slate-100">
                                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Full Content</p>
                                  <div className="bg-white p-3 rounded border text-sm text-slate-700 whitespace-pre-wrap max-h-60 overflow-y-auto">
                                    {entry.details.full_body}
                                  </div>
                                </div>
                              )}
                              
                              {/* Additional details */}
                              {(entry.details?.from || entry.details?.to || entry.details?.marked_by) && (
                                <div className="p-4 border-b border-slate-100">
                                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Details</p>
                                  <div className="space-y-1 text-sm">
                                    {entry.details.from && (
                                      <p><span className="text-slate-500">From:</span> {entry.details.from}</p>
                                    )}
                                    {entry.details.to && (
                                      <p><span className="text-slate-500">To:</span> {Array.isArray(entry.details.to) ? entry.details.to.join(', ') : entry.details.to}</p>
                                    )}
                                    {entry.details.cc && entry.details.cc.length > 0 && (
                                      <p><span className="text-slate-500">CC:</span> {entry.details.cc.join(', ')}</p>
                                    )}
                                    {entry.details.marked_by && (
                                      <p><span className="text-slate-500">Marked by:</span> {entry.details.marked_by}</p>
                                    )}
                                    {entry.details.notice_number && (
                                      <p><span className="text-slate-500">Notice:</span> {entry.details.notice_number}</p>
                                    )}
                                    {entry.details.claim_number && (
                                      <p><span className="text-slate-500">Claim:</span> {entry.details.claim_number}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Attachments */}
                              {entry.attachments?.length > 0 && (
                                <div className="p-4">
                                  <p className="text-xs font-semibold text-slate-500 uppercase mb-3">
                                    Attachments ({entry.attachments.length})
                                  </p>
                                  <div className="space-y-2">
                                    {entry.attachments.map((att, attIdx) => (
                                      <div 
                                        key={attIdx}
                                        className="flex items-center justify-between p-3 bg-white rounded border hover:border-blue-300 transition-colors"
                                      >
                                        <div className="flex items-center gap-3">
                                          {(att.filename || att.file_name || '')?.match(/\.(msg|eml)$/i) ? (
                                            <Mail className="h-5 w-5 text-blue-500" />
                                          ) : (att.filename || att.file_name || '')?.endsWith('.pdf') ? (
                                            <FileText className="h-5 w-5 text-red-500" />
                                          ) : (att.filename || att.file_name || '')?.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                            <FileText className="h-5 w-5 text-green-500" />
                                          ) : (
                                            <Paperclip className="h-5 w-5 text-slate-500" />
                                          )}
                                          <div>
                                            <p className="text-sm font-medium text-slate-700">
                                              {att.filename || att.file_name || 'Attachment'}
                                            </p>
                                            {att.size && (
                                              <p className="text-xs text-slate-400">
                                                {(att.size / 1024).toFixed(1)} KB
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        {(att.url || att.file_url) && (
                                          <a
                                            href={att.url || att.file_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`download-attachment-${attIdx}`}
                                          >
                                            <Download className="h-3 w-3" />
                                            Download
                                          </a>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Navigation links */}
                              {(entry.details?.notice_id || entry.details?.claim_id) && (
                                <div className="p-4 border-t border-slate-100">
                                  {entry.details.notice_id && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => navigate(`/notices/${entry.details.notice_id}`)}
                                      className="mr-2"
                                    >
                                      <ExternalLink className="h-4 w-4 mr-1" />
                                      View Notice
                                    </Button>
                                  )}
                                  {entry.details.claim_id && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => navigate(`/claims/${entry.details.claim_id}`)}
                                    >
                                      <ExternalLink className="h-4 w-4 mr-1" />
                                      View Claim
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'details' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
          <div className="xl:col-span-2 space-y-4 sm:space-y-6">
            {/* Email-to-Event Panel */}
            <EmailToEventPanel eventId={eventId} />
            
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
                <h2 className="text-xl font-barlow font-bold uppercase tracking-tight">Event Details</h2>
                <div className="flex items-center gap-2">
                  {/* View Event Details Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewDetailsDialogOpen(true)}
                    className="rounded-sm"
                    data-testid="view-event-details-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  
                  {/* Edit Button */}
                  {canEditEvent && !isEditing && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      className="rounded-sm"
                      data-testid="edit-event-btn"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  
                  {/* Save/Cancel Buttons */}
                  {isEditing && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEdit}
                        className="rounded-sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="rounded-sm bg-slate-900 hover:bg-slate-800"
                        data-testid="save-event-btn"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Editable Content */}
              <div className="space-y-4">
                {isEditing ? (
                  <>
                    {/* Event Family (Taxonomy) */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Event Family</p>
                      <Select 
                        value={editForm.event_family || ''} 
                        onValueChange={(v) => {
                          const familyData = editTaxonomy.find(t => t.family === v);
                          setEditSubTypes(familyData?.sub_types || []);
                          setEditForm(prev => ({ ...prev, event_family: v, event_sub_type: '', event_type: '' }));
                        }}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="edit-event-family">
                          <SelectValue placeholder="Select event family..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {editTaxonomy.map(t => (
                            <SelectItem key={t.family} value={t.family}>{t.family}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {editForm.event_family && (
                        <p className="text-xs text-slate-500 mt-1">
                          {editTaxonomy.find(t => t.family === editForm.event_family)?.description}
                        </p>
                      )}
                    </div>

                    {/* Event Sub-Type */}
                    {editForm.event_family && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Event Sub-Type</p>
                        <Select 
                          value={editForm.event_sub_type || editForm.event_type || ''} 
                          onValueChange={(v) => setEditForm(prev => ({ ...prev, event_sub_type: v, event_type: v }))}
                        >
                          <SelectTrigger className="rounded-sm" data-testid="edit-event-subtype">
                            <SelectValue placeholder="Select sub-type..." />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {(editSubTypes.length > 0 ? editSubTypes : editTaxonomy.find(t => t.family === editForm.event_family)?.sub_types || []).map(st => (
                              <SelectItem key={st} value={st}>{st}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Narrative */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Narrative *</p>
                      <Textarea
                        value={editForm.narrative}
                        onChange={(e) => setEditForm(prev => ({ ...prev, narrative: e.target.value }))}
                        rows={5}
                        className="rounded-sm"
                        placeholder="Describe the delay event in detail..."
                        data-testid="edit-narrative"
                      />
                    </div>
                    
                    {/* Contract Clause — dropdown using CONTRACT_CLAUSES */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Contract Clause Reference</p>
                      <Select 
                        value={editForm.contract_clause || ''} 
                        onValueChange={(v) => setEditForm(prev => ({ ...prev, contract_clause: v }))}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="edit-contract-clause">
                          <SelectValue placeholder="Select contract clause..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {(CONTRACT_CLAUSES[projectContractType] || CONTRACT_CLAUSES.JBCC || []).map((clause) => (
                            <SelectItem key={clause.id} value={`${clause.clause} - ${clause.title}`}>
                              {clause.clause} — {clause.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Read-only taxonomy display */}
                    {event.event_family && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Event Family</p>
                        <Badge className="bg-slate-100 text-slate-700 border-slate-200">{event.event_family}</Badge>
                        {event.event_sub_type && event.event_sub_type !== event.event_family && (
                          <span className="ml-2 text-sm text-slate-600">{event.event_sub_type}</span>
                        )}
                      </div>
                    )}
                    {!event.event_family && event.event_type && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Event Type</p>
                        <p className="text-slate-700">{event.event_type}</p>
                      </div>
                    )}
                    {event.event_source && event.event_source !== 'manual' && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Event Source</p>
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200 uppercase text-xs">{event.event_source.replace(/_/g, ' ')}</Badge>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Narrative</p>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{event.narrative}</p>
                    </div>
                    {event.contract_clause && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Contract Clause</p>
                        <p className="text-slate-700 font-mono">{event.contract_clause}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* === CONTRACTUAL CLASSIFICATION SECTION === */}
            <ContractualClassificationPanel
              event={event}
              projectContractType={projectContractType}
              canEditEvent={canEditEvent}
              onClassified={() => loadEvent()}
              contractClauses={CONTRACT_CLAUSES}
            />
          </div>

          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
              <h3 className="text-lg font-barlow font-bold uppercase tracking-tight mb-4 pb-4 border-b border-slate-100">Information</h3>
              <div className="space-y-3">
                {isEditing ? (
                  <>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Responsible Party *</p>
                      <Select 
                        value={editForm.responsible_party} 
                        onValueChange={(v) => setEditForm(prev => ({ ...prev, responsible_party: v }))}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="Select responsible party" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Contractor">Contractor</SelectItem>
                          <SelectItem value="Consultant">Consultant</SelectItem>
                          <SelectItem value="Employer">Employer</SelectItem>
                          <SelectItem value="Subcontractor">Subcontractor</SelectItem>
                          <SelectItem value="Third Party">Third Party</SelectItem>
                          <SelectItem value="Force Majeure">Force Majeure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Responsible Party Detail</p>
                      <Input
                        value={editForm.responsible_party_detail}
                        onChange={(e) => setEditForm(prev => ({ ...prev, responsible_party_detail: e.target.value }))}
                        className="rounded-sm"
                        placeholder="e.g., Architect, Structural Engineer"
                      />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Start Date *</p>
                      <Input
                        type="date"
                        value={editForm.start_date}
                        onChange={(e) => setEditForm(prev => ({ ...prev, start_date: e.target.value }))}
                        className="rounded-sm"
                        data-testid="edit-start-date"
                      />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">End Date</p>
                      <Input
                        type="date"
                        value={editForm.end_date}
                        onChange={(e) => setEditForm(prev => ({ ...prev, end_date: e.target.value }))}
                        className="rounded-sm"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Responsible Party</p>
                      <p className="text-slate-700 font-medium">{event.responsible_party}</p>
                      {event.responsible_party_detail && (
                        <p className="text-sm text-slate-500">{event.responsible_party_detail}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Start Date</p>
                      <p className="text-slate-700 font-mono">{new Date(event.start_date).toLocaleDateString()}</p>
                    </div>
                    {event.end_date && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">End Date</p>
                        <p className="text-slate-700 font-mono">{new Date(event.end_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Created</p>
                      <p className="text-slate-700 font-mono text-sm">{new Date(event.created_at).toLocaleString()}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Critical Path Assessment */}
            <div className={`border rounded-sm shadow-sm p-6 ${
              event.still_impacting_programme !== false 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">Critical Path Assessment</h3>
                {event.still_impacting_programme !== false ? (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">On Critical Path</Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">Non-Critical</Badge>
                )}
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Is this delay event currently on the critical path of the programme?
              </p>
              <div className="flex items-center justify-between">
                <Label 
                  htmlFor="programme-impact-toggle" 
                  className={`text-sm font-medium ${
                    event.still_impacting_programme !== false ? 'text-amber-700' : 'text-green-700'
                  }`}
                >
                  {event.still_impacting_programme !== false ? 'On Critical Path' : 'Non-Critical / Absorbed'}
                </Label>
                {canEditEvent && (
                  <Switch
                    id="programme-impact-toggle"
                    data-testid="programme-impact-toggle"
                    checked={event.still_impacting_programme !== false}
                    onCheckedChange={handleToggleProgrammeImpact}
                    disabled={togglingImpact}
                  />
                )}
              </div>

              {/* Show history of critical path changes */}
              {(event.programme_impact_notes || event.impact_reason_category) && (
                <div className="mt-3 border-t pt-3 space-y-1">
                  {event.impact_reason_category && (
                    <p className="text-xs text-slate-600">
                      <span className="font-semibold">Category:</span>{' '}
                      {event.impact_reason_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  )}
                  {event.programme_impact_notes && (
                    <p className="text-xs text-slate-500">
                      <span className="font-semibold">Notes:</span> {event.programme_impact_notes}
                    </p>
                  )}
                  {event.programme_impact_ended_date && (
                    <p className="text-xs text-slate-500">
                      <span className="font-semibold">Date impact ended:</span>{' '}
                      {new Date(event.programme_impact_ended_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {/* Timeline entries for critical path changes */}
              {event.timeline?.filter(t => t.type === 'critical_path_assessment').length > 0 && (
                <div className="mt-3 border-t pt-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Assessment History</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {event.timeline.filter(t => t.type === 'critical_path_assessment').reverse().map((entry, idx) => (
                      <div key={entry.id || idx} className="text-xs p-2 bg-white/60 rounded-sm border border-slate-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-semibold ${entry.action?.includes('Restored') ? 'text-amber-600' : 'text-green-600'}`}>
                            {entry.action}
                          </span>
                          <span className="text-slate-400">{new Date(entry.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-slate-500 whitespace-pre-line">{entry.detail}</p>
                        <p className="text-slate-400 mt-1">by {entry.created_by}</p>
                        {entry.attachments?.length > 0 && (
                          <p className="text-blue-500 mt-1">{entry.attachments.length} file(s) attached</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Critical Path Assessment Dialog */}
            <Dialog open={programmeImpactDialogOpen} onOpenChange={setProgrammeImpactDialogOpen}>
              <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-lg font-barlow font-bold uppercase">
                    {event.still_impacting_programme !== false
                      ? 'Remove from Critical Path'
                      : 'Restore to Critical Path'
                    }
                  </DialogTitle>
                  <DialogDescription>
                    {event.still_impacting_programme !== false
                      ? 'Document why this delay is no longer affecting the critical path. This will be recorded in the timeline.'
                      : 'Document why this delay is being restored to the critical path.'
                    }
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-3">
                  {/* Reason Category (only when removing from CP) */}
                  {event.still_impacting_programme !== false && (
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Reason Category *</Label>
                      <Select value={impactReasonCategory} onValueChange={setImpactReasonCategory}>
                        <SelectTrigger className="rounded-sm" data-testid="impact-reason-category">
                          <SelectValue placeholder="Select reason..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="float_absorbed">Float absorbed the delay</SelectItem>
                          <SelectItem value="activities_resequenced">Activities re-sequenced / mitigated</SelectItem>
                          <SelectItem value="acceleration_measures">Acceleration measures implemented</SelectItem>
                          <SelectItem value="programme_revised">Programme revised — critical path shifted</SelectItem>
                          <SelectItem value="delay_resolved">Delay event resolved</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Date Impact Ended (only when removing from CP) */}
                  {event.still_impacting_programme !== false && (
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Date Impact Ended</Label>
                      <Input
                        type="date"
                        value={programmeImpactDate}
                        onChange={(e) => setProgrammeImpactDate(e.target.value)}
                        className="rounded-sm font-mono"
                        data-testid="impact-end-date"
                      />
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Notes / Justification *</Label>
                    <Textarea
                      value={programmeImpactReason}
                      onChange={(e) => setProgrammeImpactReason(e.target.value)}
                      placeholder={event.still_impacting_programme !== false 
                        ? "Explain why this delay is no longer on the critical path..."
                        : "Explain why this delay is being restored to the critical path..."
                      }
                      className="rounded-sm min-h-[100px]"
                      data-testid="impact-notes"
                    />
                  </div>

                  {/* File Upload (drag and drop) */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Supporting Evidence</Label>
                    <div
                      className="border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-sm p-6 text-center cursor-pointer transition-colors"
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-400', 'bg-blue-50'); }}
                      onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50'); }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
                        const files = Array.from(e.dataTransfer.files);
                        for (const file of files) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await api.events.uploadDocument(eventId, formData);
                            const fileId = res.data?.file_id || res.data?.id || file.name;
                            setImpactAttachmentIds(prev => [...prev, fileId]);
                            toast.success(`Uploaded: ${file.name}`);
                          } catch { toast.error(`Failed to upload: ${file.name}`); }
                        }
                      }}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.multiple = true;
                        input.onchange = async (e) => {
                          const files = Array.from(e.target.files);
                          for (const file of files) {
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              const res = await api.events.uploadDocument(eventId, formData);
                              const fileId = res.data?.file_id || res.data?.id || file.name;
                              setImpactAttachmentIds(prev => [...prev, fileId]);
                              toast.success(`Uploaded: ${file.name}`);
                            } catch { toast.error(`Failed to upload: ${file.name}`); }
                          }
                        };
                        input.click();
                      }}
                      data-testid="impact-file-drop-zone"
                    >
                      <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Drag & drop files here or click to browse</p>
                      <p className="text-xs text-slate-400 mt-1">Revised programme, time impact analysis, mitigation plan, etc.</p>
                    </div>
                    {impactAttachmentIds.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {impactAttachmentIds.map((id, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-slate-100 p-2 rounded-sm">
                            <span className="text-slate-600 truncate flex-1">{id}</span>
                            <button className="text-red-400 hover:text-red-600 ml-2" onClick={() => setImpactAttachmentIds(prev => prev.filter((_, i) => i !== idx))}>
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setProgrammeImpactDialogOpen(false)}
                    className="flex-1 rounded-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmImpactChange}
                    disabled={togglingImpact || !programmeImpactReason.trim() || (event.still_impacting_programme !== false && !impactReasonCategory)}
                    className={`flex-1 rounded-sm text-white ${
                      event.still_impacting_programme !== false 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-amber-600 hover:bg-amber-700'
                    }`}
                    data-testid="confirm-impact-change-btn"
                  >
                    {togglingImpact ? 'Saving...' : (
                      event.still_impacting_programme !== false 
                        ? 'Confirm — Remove from Critical Path' 
                        : 'Confirm — Restore to Critical Path'
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Notice Readiness Checklist */}
            {noticeReadiness && !event.notice_id && (
              <div className={`border rounded-sm shadow-sm p-5 ${
                noticeReadiness.ready_for_notice ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'
              }`} data-testid="notice-readiness-widget">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">
                  Notice Readiness
                  <span className="ml-2 text-xs font-mono text-slate-500">
                    {noticeReadiness.checks_passed}/{noticeReadiness.checks_total}
                  </span>
                </h3>
                <div className="space-y-1.5">
                  {noticeReadiness.checks.map((check, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {check.passed ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                      ) : check.required ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                      ) : (
                        <Info className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                      )}
                      <span className={`text-xs ${check.passed ? 'text-green-700' : check.required ? 'text-red-700' : 'text-slate-500'}`}>
                        {check.check}
                      </span>
                    </div>
                  ))}
                </div>
                {noticeReadiness.ready_for_notice && (
                  <p className="text-xs text-green-700 mt-3 font-medium">All checks passed — ready for notice generation.</p>
                )}
              </div>
            )}

            {event.status === 'draft' && (
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-6">
                <h4 className="text-sm font-bold uppercase tracking-wider text-orange-700 mb-2">Next Steps</h4>
                <p className="text-sm text-orange-600">
                  Convert this event to a formal notice within 20 days of becoming aware of the delay (JBCC requirement).
                </p>
              </div>
            )}
          </div>
        </div>
        )}

        {activeTab === 'correspondence' && (
          <UnifiedCommunicationLog 
            projectId={event.project_id}
            moduleType="DelayEvent"
            moduleEntityId={eventId}
            title="Communication Log"
          />
        )}

        {activeTab === 'narrative' && (
          <TimelineNarrativePanel eventId={eventId} />
        )}

        {activeTab === 'emails' && (
          <EmailHistoryPanel 
            projectId={event.project_id} 
            documentType="event" 
            documentId={eventId} 
          />
        )}

        {/* View Event Details Dialog */}
        <Dialog open={viewDetailsDialogOpen} onOpenChange={setViewDetailsDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-barlow font-bold uppercase flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Event Details - {event.event_number}
              </DialogTitle>
              <DialogDescription>
                Complete details of this delay event
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Status Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge className={`rounded-sm ${event.status === 'active' ? 'bg-blue-100 text-blue-700 border-blue-200' : event.status === 'ceased' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-slate-100 text-slate-700'}`}>
                  {event.status?.toUpperCase() || 'ACTIVE'}
                </Badge>
                <Badge className={`rounded-sm ${event.still_impacting_programme !== false ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-green-100 text-green-700 border-green-200'}`}>
                  {event.still_impacting_programme !== false ? 'Impacting Programme' : 'No Programme Impact'}
                </Badge>
                {event.has_notice && <Badge className="rounded-sm bg-purple-100 text-purple-700 border-purple-200">Has Notice</Badge>}
                {event.has_claim && <Badge className="rounded-sm bg-indigo-100 text-indigo-700 border-indigo-200">Has Claim</Badge>}
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-sm">
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500">Event Number</p>
                  <p className="text-lg font-mono font-bold text-slate-900">{event.event_number}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500">Event Type</p>
                  <p className="text-lg font-medium text-slate-900">{event.event_type}</p>
                </div>
                {event.event_family && (
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Event Family</p>
                    <p className="text-sm font-medium text-slate-900">{event.event_family}</p>
                  </div>
                )}
                {event.event_sub_type && event.event_sub_type !== event.event_family && (
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Event Sub-Type</p>
                    <p className="text-sm text-slate-700">{event.event_sub_type}</p>
                  </div>
                )}
                {event.event_source && event.event_source !== 'manual' && (
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Source</p>
                    <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs uppercase">
                      {event.event_source.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                )}
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500">Responsible Party</p>
                  <p className="text-slate-900">{event.responsible_party}</p>
                  {event.responsible_party_detail && (
                    <p className="text-sm text-slate-500">{event.responsible_party_detail}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500">Contract Clause</p>
                  <p className="text-slate-900 font-mono">{event.contract_clause || 'Not specified'}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-sm border border-blue-200">
                  <p className="text-xs font-bold uppercase text-blue-600">Start Date</p>
                  <p className="text-lg font-mono text-blue-900">{new Date(event.start_date).toLocaleDateString()}</p>
                </div>
                <div className={`p-4 rounded-sm border ${event.end_date ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`text-xs font-bold uppercase ${event.end_date ? 'text-green-600' : 'text-slate-500'}`}>End Date</p>
                  <p className={`text-lg font-mono ${event.end_date ? 'text-green-900' : 'text-slate-400'}`}>
                    {event.end_date ? new Date(event.end_date).toLocaleDateString() : 'Ongoing'}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-sm border border-slate-200">
                  <p className="text-xs font-bold uppercase text-slate-500">Duration</p>
                  <p className="text-lg font-mono text-slate-900">
                    {event.end_date 
                      ? `${Math.ceil((new Date(event.end_date) - new Date(event.start_date)) / (1000 * 60 * 60 * 24))} days`
                      : `${Math.ceil((new Date() - new Date(event.start_date)) / (1000 * 60 * 60 * 24))} days (ongoing)`}
                  </p>
                </div>
              </div>

              {/* Narrative */}
              <div>
                <p className="text-xs font-bold uppercase text-slate-500 mb-2">Narrative / Description</p>
                <div className="bg-white border border-slate-200 p-4 rounded-sm">
                  <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{event.narrative}</p>
                </div>
              </div>

              {/* Programme Impact Notes */}
              {event.programme_impact_notes && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-sm">
                  <p className="text-xs font-bold uppercase text-green-600 mb-2">Programme Impact Resolution</p>
                  <p className="text-green-800">{event.programme_impact_notes}</p>
                  {event.programme_impact_ended_date && (
                    <p className="text-sm text-green-600 mt-2">
                      Ended: {new Date(event.programme_impact_ended_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {/* Cease Information */}
              {event.status === 'ceased' && event.ceased_date && (
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-sm">
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Cease Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Ceased Date</p>
                      <p className="text-slate-900 font-mono">{new Date(event.ceased_date).toLocaleDateString()}</p>
                    </div>
                    {event.ceased_reason && (
                      <div>
                        <p className="text-xs text-slate-500">Reason</p>
                        <p className="text-slate-900">{event.ceased_reason}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Audit Info */}
              <div className="grid grid-cols-2 gap-4 text-sm text-slate-500 border-t border-slate-200 pt-4">
                <div>
                  <p className="text-xs font-bold uppercase">Created</p>
                  <p>{new Date(event.created_at).toLocaleString()}</p>
                  {event.created_by_name && <p className="text-xs">by {event.created_by_name}</p>}
                </div>
                <div>
                  <p className="text-xs font-bold uppercase">Last Updated</p>
                  <p>{new Date(event.updated_at || event.created_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDetailsDialogOpen(false)}>
                Close
              </Button>
              {canEditEvent && (
                <Button 
                  onClick={() => {
                    setViewDetailsDialogOpen(false);
                    setIsEditing(true);
                  }}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Event
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}